/**
 * Alerts Tab Component - Critical Alert System
 * Monitors system health and displays active/historical alerts
 */
import { useState, useEffect, useCallback } from 'react';
import { 
    Bell, AlertTriangle, Clock, CheckCircle, Loader2, Heart,
    Server, Database, Mail, TrendingUp, Cpu, Zap, Sparkles,
    Filter, RefreshCw, Calendar, BarChart3
} from 'lucide-react';
import { getAuthHeaders } from '../../utils/api';
import { toast } from 'sonner';
import { BentoCard, NeonIcon, API_URL } from './shared';

const AlertsTab = ({ isSpanish }) => {
    const [alerts, setAlerts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeView, setActiveView] = useState('active');
    const [acknowledging, setAcknowledging] = useState({});
    const [testingAlert, setTestingAlert] = useState(null);
    const [runningHealthCheck, setRunningHealthCheck] = useState(false);
    const [healthCheckResult, setHealthCheckResult] = useState(null);
    
    const alertTypeMeta = {
        api_down: { icon: Server, color: 'red', label: 'API Down' },
        error_spike: { icon: AlertTriangle, color: 'red', label: 'Error Spike' },
        gemini_failure: { icon: Sparkles, color: 'red', label: 'Gemini AI Failure' },
        database_error: { icon: Database, color: 'red', label: 'Database Error' },
        email_service_down: { icon: Mail, color: 'red', label: 'Email Service Down' },
        high_bounce_rate: { icon: Mail, color: 'yellow', label: 'High Bounce Rate' },
        traffic_spike: { icon: TrendingUp, color: 'yellow', label: 'Traffic Spike' },
        cache_degradation: { icon: Cpu, color: 'yellow', label: 'Cache Degradation' },
        rate_limit_triggered: { icon: Clock, color: 'blue', label: 'Rate Limit Triggered' },
        cold_start_spike: { icon: Zap, color: 'yellow', label: 'Cold Start Spike' }
    };
    
    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical': return 'text-red-400 bg-red-500/20 border-red-500/30';
            case 'warning': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
            case 'info': return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
            default: return 'text-white/60 bg-white/10 border-white/20';
        }
    };
    
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const headers = await getAuthHeaders();
            const [alertsRes, summaryRes, configRes] = await Promise.all([
                fetch(`${API_URL}/api/admin/alerts?${activeView === 'active' ? 'active_only=true' : 'days=7'}`, { headers, credentials: 'include' }),
                fetch(`${API_URL}/api/admin/alerts/summary`, { headers, credentials: 'include' }),
                fetch(`${API_URL}/api/admin/alerts/config`, { headers, credentials: 'include' })
            ]);
            
            if (alertsRes.ok) setAlerts((await alertsRes.json()).alerts || []);
            if (summaryRes.ok) setSummary(await summaryRes.json());
            if (configRes.ok) setConfig((await configRes.json()).config || {});
        } catch (e) {
            console.error('Alerts fetch error:', e);
            toast.error('Failed to load alerts');
        } finally {
            setLoading(false);
        }
    }, [activeView]);
    
    useEffect(() => { fetchData(); }, [fetchData]);
    
    const handleAcknowledge = async (alertType) => {
        setAcknowledging(prev => ({ ...prev, [alertType]: true }));
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/admin/alerts/${alertType}/acknowledge`, { method: 'POST', headers, credentials: 'include' });
            if (res.ok) { toast.success(`Alert acknowledged: ${alertType}`); fetchData(); }
            else toast.error('Failed to acknowledge alert');
        } catch (e) {
            toast.error('Failed to acknowledge alert');
        } finally {
            setAcknowledging(prev => ({ ...prev, [alertType]: false }));
        }
    };
    
    const handleTestAlert = async (alertType) => {
        setTestingAlert(alertType);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/admin/alerts/test/${alertType}`, { method: 'POST', headers, credentials: 'include' });
            const data = await res.json();
            if (data.success) { toast.success(`Test alert sent: ${alertType}${data.email_sent ? ' (email sent)' : ''}`); fetchData(); }
            else toast.error('Failed to send test alert');
        } catch (e) {
            toast.error('Failed to send test alert');
        } finally {
            setTestingAlert(null);
        }
    };
    
    const handleRunHealthCheck = async () => {
        setRunningHealthCheck(true);
        setHealthCheckResult(null);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/admin/health-check/run`, { method: 'POST', headers, credentials: 'include' });
            const data = await res.json();
            setHealthCheckResult(data);
            if (data.alerts_triggered?.length > 0) toast.warning(`Health check complete - ${data.alerts_triggered.length} alerts triggered`);
            else toast.success('Health check complete - all systems healthy');
            fetchData();
        } catch (e) {
            toast.error('Failed to run health check');
        } finally {
            setRunningHealthCheck(false);
        }
    };
    
    const formatTime = (timestamp) => timestamp ? new Date(timestamp).toLocaleString() : 'Unknown';
    
    const timeAgo = (timestamp) => {
        if (!timestamp) return '';
        const diffMs = new Date() - new Date(timestamp);
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays > 0) return `${diffDays}d ago`;
        if (diffHours > 0) return `${diffHours}h ago`;
        if (diffMins > 0) return `${diffMins}m ago`;
        return 'Just now';
    };

    return (
        <div className="space-y-6" data-testid="alerts-tab">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <BentoCard highlight={summary?.active_count > 0}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-white/50 uppercase tracking-wider">{isSpanish ? 'Alertas Activas' : 'Active Alerts'}</p>
                            <p className={`text-3xl font-bold ${summary?.active_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                {loading ? '...' : summary?.active_count || 0}
                            </p>
                        </div>
                        <Bell className={`w-8 h-8 ${summary?.active_count > 0 ? 'text-red-400/50 animate-pulse' : 'text-green-400/50'}`} style={summary?.active_count > 0 ? { filter: 'drop-shadow(0 0 4px #FF0040) drop-shadow(0 0 8px #FF0040)' } : { filter: 'drop-shadow(0 0 4px #00FF7F) drop-shadow(0 0 8px #00FF7F)' }} />
                    </div>
                </BentoCard>
                
                <BentoCard>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-white/50 uppercase tracking-wider">{isSpanish ? 'Críticas (24h)' : 'Critical (24h)'}</p>
                            <p className={`text-3xl font-bold ${summary?.critical_24h > 0 ? 'text-red-400' : 'text-white'}`}>
                                {loading ? '...' : summary?.critical_24h || 0}
                            </p>
                        </div>
                        <AlertTriangle className={`w-8 h-8 ${summary?.critical_24h > 0 ? 'text-red-400/50' : 'text-white/20'}`} style={summary?.critical_24h > 0 ? { filter: 'drop-shadow(0 0 4px #FF0040) drop-shadow(0 0 8px #FF0040)' } : {}} />
                    </div>
                </BentoCard>
                
                <BentoCard>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-white/50 uppercase tracking-wider">{isSpanish ? 'Advertencias (24h)' : 'Warnings (24h)'}</p>
                            <p className={`text-3xl font-bold ${summary?.warning_24h > 0 ? 'text-yellow-400' : 'text-white'}`}>
                                {loading ? '...' : summary?.warning_24h || 0}
                            </p>
                        </div>
                        <AlertTriangle className={`w-8 h-8 ${summary?.warning_24h > 0 ? 'text-yellow-400/50' : 'text-white/20'}`} style={summary?.warning_24h > 0 ? { filter: 'drop-shadow(0 0 4px #FFFF00) drop-shadow(0 0 8px #FFFF00)' } : {}} />
                    </div>
                </BentoCard>
                
                <BentoCard>
                    <div className="flex flex-col h-full justify-between">
                        <p className="text-xs text-white/50 uppercase tracking-wider mb-2">{isSpanish ? 'Verificación Manual' : 'Manual Check'}</p>
                        <button onClick={handleRunHealthCheck} disabled={runningHealthCheck}
                            className="w-full py-2 px-4 rounded-lg bg-primary/20 text-primary border border-primary/30 hover:bg-primary/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            data-testid="run-health-check-btn">
                            {runningHealthCheck ? <Loader2 className="w-4 h-4 animate-spin" /> : <NeonIcon icon={Heart} neonColor="pink" size="w-4 h-4" />}
                            {isSpanish ? 'Ejecutar' : 'Run Check'}
                        </button>
                    </div>
                </BentoCard>
            </div>
            
            {/* Health Check Result */}
            {healthCheckResult && (
                <BentoCard className={healthCheckResult.alerts_triggered?.length > 0 ? 'border-yellow-500/30' : 'border-green-500/30'}>
                    <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                        <NeonIcon icon={Heart} neonColor="pink" size="w-4 h-4" />
                        {isSpanish ? 'Resultado de Verificación' : 'Health Check Result'}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        {Object.entries(healthCheckResult.checks || {}).map(([service, result]) => (
                            <div key={service} className="bg-black/20 rounded-lg p-3">
                                <p className="text-white/50 text-xs uppercase mb-1">{service}</p>
                                <p className={`font-medium ${result.status === 'healthy' ? 'text-green-400' : result.status === 'degraded' ? 'text-yellow-400' : 'text-red-400'}`}>
                                    {result.status || 'unknown'}
                                </p>
                                {result.latency_ms && <p className="text-white/40 text-xs">{result.latency_ms}ms</p>}
                            </div>
                        ))}
                    </div>
                    {healthCheckResult.alerts_triggered?.length > 0 && (
                        <div className="mt-3 p-2 bg-red-500/10 rounded-lg border border-red-500/20">
                            <p className="text-red-400 text-sm"><AlertTriangle className="w-4 h-4 inline mr-1" />Alerts triggered: {healthCheckResult.alerts_triggered.join(', ')}</p>
                        </div>
                    )}
                </BentoCard>
            )}
            
            {/* Sub-navigation */}
            <div className="flex gap-2 border-b border-white/10 pb-2">
                {[
                    { id: 'active', label: isSpanish ? 'Activas' : 'Active', icon: Bell },
                    { id: 'history', label: isSpanish ? 'Historial' : 'History', icon: Clock },
                    { id: 'config', label: isSpanish ? 'Configuración' : 'Configuration', icon: Filter }
                ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveView(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-all ${activeView === tab.id ? 'bg-white/10 text-white border-b-2 border-primary' : 'text-white/50 hover:text-white/70'}`}
                        data-testid={`alerts-view-${tab.id}`}>
                        <tab.icon className="w-4 h-4" />{tab.label}
                    </button>
                ))}
                <button onClick={fetchData} className="ml-auto p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-all" data-testid="refresh-alerts-btn">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>
            
            {/* Active Alerts View */}
            {activeView === 'active' && (
                <div className="space-y-4">
                    {loading ? (
                        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" /><p className="text-white/50 mt-2">{isSpanish ? 'Cargando...' : 'Loading...'}</p></div>
                    ) : alerts.length === 0 ? (
                        <div className="text-center py-12" data-testid="no-active-alerts">
                            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-white mb-2">{isSpanish ? '¡Todo está bien!' : 'All Clear!'}</h3>
                            <p className="text-white/50">{isSpanish ? 'No hay alertas activas en este momento.' : 'No active alerts at the moment.'}</p>
                        </div>
                    ) : (
                        alerts.map((alert, idx) => {
                            const meta = alertTypeMeta[alert.alert_type] || { icon: Bell, color: 'white', label: alert.alert_type };
                            const IconComponent = meta.icon;
                            return (
                                <BentoCard key={idx} className={`${getSeverityColor(alert.severity)} border`} data-testid={`alert-item-${alert.alert_type}`}>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-3 flex-1">
                                            <IconComponent className={`w-6 h-6 mt-1 text-${meta.color}-400`} />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h4 className="font-bold text-white">{meta.label}</h4>
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityColor(alert.severity)}`}>{alert.severity?.toUpperCase()}</span>
                                                    {alert.acknowledged && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">{isSpanish ? 'Reconocida' : 'Acknowledged'}</span>}
                                                </div>
                                                <p className="text-white/70 text-sm mb-2">{alert.message}</p>
                                                {alert.details && Object.keys(alert.details).length > 0 && (
                                                    <div className="bg-black/20 rounded-lg p-2 text-xs space-y-1">
                                                        {Object.entries(alert.details).map(([key, value]) => <p key={key} className="text-white/50"><span className="text-white/70">{key}:</span> {JSON.stringify(value)}</p>)}
                                                    </div>
                                                )}
                                                <p className="text-white/40 text-xs mt-2">{formatTime(alert.timestamp)} • {timeAgo(alert.timestamp)}</p>
                                            </div>
                                        </div>
                                        {!alert.acknowledged && !alert.resolved && (
                                            <button onClick={() => handleAcknowledge(alert.alert_type)} disabled={acknowledging[alert.alert_type]}
                                                className="px-3 py-1.5 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20 transition-all flex items-center gap-1 disabled:opacity-50"
                                                data-testid={`acknowledge-${alert.alert_type}`}>
                                                {acknowledging[alert.alert_type] ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                                {isSpanish ? 'Reconocer' : 'Acknowledge'}
                                            </button>
                                        )}
                                    </div>
                                </BentoCard>
                            );
                        })
                    )}
                </div>
            )}
            
            {/* History View */}
            {activeView === 'history' && (
                <div className="space-y-4">
                    {loading ? (
                        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" /></div>
                    ) : alerts.length === 0 ? (
                        <div className="text-center py-12" data-testid="no-alert-history">
                            <Clock className="w-16 h-16 text-white/20 mx-auto mb-4" />
                            <p className="text-white/50">{isSpanish ? 'No hay historial de alertas.' : 'No alert history found.'}</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {alerts.map((alert, idx) => {
                                const meta = alertTypeMeta[alert.alert_type] || { icon: Bell, color: 'white', label: alert.alert_type };
                                const IconComponent = meta.icon;
                                return (
                                    <div key={idx} className={`p-3 rounded-lg bg-black/20 border border-white/10 ${alert.resolved ? 'opacity-60' : ''}`} data-testid={`history-item-${idx}`}>
                                        <div className="flex items-center gap-3">
                                            <IconComponent className={`w-5 h-5 text-${meta.color}-400`} />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-white text-sm">{meta.label}</span>
                                                    <span className={`text-xs px-1.5 py-0.5 rounded ${getSeverityColor(alert.severity)}`}>{alert.severity}</span>
                                                    {alert.resolved && <span className="text-xs text-green-400">✓ Resolved</span>}
                                                    {alert.email_sent && <span className="text-xs text-blue-400">📧 Emailed</span>}
                                                </div>
                                            </div>
                                            <span className="text-white/40 text-xs">{timeAgo(alert.timestamp)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            
            {/* Configuration View */}
            {activeView === 'config' && (
                <div className="space-y-4" data-testid="alerts-config-view">
                    <BentoCard>
                        <h4 className="font-bold text-white mb-4 flex items-center gap-2">
                            <NeonIcon icon={Filter} neonColor="cyan" size="w-4 h-4" />
                            {isSpanish ? 'Configuración de Alertas' : 'Alert Configuration'}
                        </h4>
                        <p className="text-white/50 text-sm mb-4">
                            {isSpanish ? 'Configure umbrales y notificaciones para cada tipo de alerta.' : 'Configure thresholds and notifications for each alert type.'}
                        </p>
                        <div className="space-y-3">
                            {Object.entries(config || {}).map(([alertType, alertConfig]) => {
                                const meta = alertTypeMeta[alertType] || { icon: Bell, color: 'white', label: alertType };
                                const IconComponent = meta.icon;
                                return (
                                    <div key={alertType} className="p-4 rounded-lg bg-black/20 border border-white/10" data-testid={`config-${alertType}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <IconComponent className={`w-5 h-5 text-${meta.color}-400`} />
                                                <span className="font-medium text-white">{meta.label}</span>
                                                <span className={`text-xs px-1.5 py-0.5 rounded ${getSeverityColor(alertConfig.severity)}`}>{alertConfig.severity}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs ${alertConfig.email_enabled ? 'text-green-400' : 'text-white/40'}`}>
                                                    {alertConfig.email_enabled ? '📧 Email ON' : '📧 Email OFF'}
                                                </span>
                                                <button onClick={() => handleTestAlert(alertType)} disabled={testingAlert === alertType}
                                                    className="px-2 py-1 text-xs rounded bg-white/10 text-white/70 hover:bg-white/20 transition-all disabled:opacity-50"
                                                    data-testid={`test-alert-${alertType}`}>
                                                    {testingAlert === alertType ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Test'}
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-white/50 text-sm mb-2">{alertConfig.description}</p>
                                        <div className="flex gap-4 text-xs text-white/40">
                                            <span>Threshold: {alertConfig.threshold}</span>
                                            <span>Cooldown: {alertConfig.cooldown_minutes} min</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </BentoCard>
                    
                    <BentoCard>
                        <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                            <NeonIcon icon={Calendar} neonColor="blue" size="w-4 h-4" />
                            {isSpanish ? 'Tareas Programadas Requeridas' : 'Required Scheduled Tasks'}
                        </h4>
                        <p className="text-white/50 text-sm mb-4">
                            {isSpanish ? 'Para que el sistema de alertas funcione automáticamente, configure estas tareas en Cloud Scheduler:' : 'For the alert system to work automatically, set up these Cloud Scheduler jobs:'}
                        </p>
                        <div className="space-y-3">
                            <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                                <div className="flex items-center gap-2 mb-1">
                                    <NeonIcon icon={Heart} neonColor="green" size="w-4 h-4" />
                                    <span className="font-medium text-white text-sm">Health Check</span>
                                    <span className="text-xs text-primary bg-primary/20 px-2 py-0.5 rounded">Every 5 min</span>
                                </div>
                                <code className="text-xs text-white/60 block mt-1">POST /api/internal/tasks/health-check</code>
                            </div>
                            <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                                <div className="flex items-center gap-2 mb-1">
                                    <NeonIcon icon={BarChart3} neonColor="blue" size="w-4 h-4" />
                                    <span className="font-medium text-white text-sm">Weekly Health Report</span>
                                    <span className="text-xs text-primary bg-primary/20 px-2 py-0.5 rounded">Weekly (Mon 9AM)</span>
                                </div>
                                <code className="text-xs text-white/60 block mt-1">POST /api/internal/tasks/weekly-health-report</code>
                            </div>
                        </div>
                    </BentoCard>
                </div>
            )}
        </div>
    );
};

export default AlertsTab;
